import { Box } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { CarrierVisitStatus } from '@planning/app/api'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { useTranslate } from '@tolgee/react'
import { ButtonOption, OptionsButton } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'

interface Props {
  visit: IRailVisitItem
  onDelete: (id: number) => Promise<void>
}

export const RailVisitTableOptions = observer(({ visit, onDelete }: Props) => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const { appViewStore } = usePlanningStore()

  const { status } = visit.data

  const isCancelled = status === CarrierVisitStatus.Cancelled
  const isExpected = status === CarrierVisitStatus.Expected
  const isClosed = status === CarrierVisitStatus.Closed

  const canEdit = !isClosed && !isCancelled
  const canCancel = isExpected

  const options: ButtonOption[] = [
    {
      label: t('view', 'View'),
      onClick: () => navigate(`${PATHS.visits.railVisits.url}/${visit.id}/dashboard`),
      testId: 'view-visit-option',
    },
    {
      label: t('edit', 'Edit'),
      onClick: () => navigate(`${PATHS.visits.railPlanning.url}/${visit.id}`),
      testId: 'edit-visit-option',
      disabled: !canEdit,
    },
    {
      label: t('cancelVisit', 'Cancel visit'),
      onClick: () => {
        handleDelete()
      },
      testId: 'delete-visit-option',
      disabled: !canCancel,
    },
  ]

  const handleDelete = async () => {
    const isConfirmed = await appViewStore.setOpenConfirmDialog(
      true,
      t(
        'cancellingVisitWillRemoveInfo',
        'Cancelling this visit will remove all related information.',
      ),
      t('cancelRailVisit', 'Cancel rail visit?'),
      undefined,
      {
        primaryActionText: t('yesCancel', 'Yes, cancel'),
        secondaryActionText: t('noKeepVisit', 'No, keep visit'),
      },
    )

    if (isConfirmed) {
      try {
        await onDelete(visit.id)
        appViewStore.setShowAlert(
          'success',
          t('railVisitCancelledSuccessfully', 'Rail Visit cancelled successfully'),
        )
      } catch {
        appViewStore.setShowAlert('error', t('failedToCancel', 'Failed to cancel'))
      }
    }
  }

  return (
    <Box onClick={e => e.stopPropagation()}>
      <OptionsButton
        tooltip={t('moreOptions', 'More options')}
        options={options}
        position='right'
        testId={'rail-visit-three-dot-menu'}
      />
    </Box>
  )
})
